/* eslint-disable no-console */
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AMPLITUDE_API_KEY, devEnv, isProduction } from '#src/config.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { loadCookie } from '#src/lib/cookies.ts';

import { AnalyticsContext } from './index.ts';
import { useRecordPath } from './useRecordPath.tsx';

type Props = {
  children: ReactNode;
};

let sessionReplay:
  | typeof import('@amplitude/session-replay-browser')
  | undefined;

declare global {
  interface Window {
    gtag: (
      type: string,
      event: string,
      options: { [key: string]: any }
    ) => void | undefined;
    analytics: {
      ready: (fn: () => void) => void;
      identify: (userId: string, traits?: { [trait: string]: any }) => void;
      track: (
        eventName: string,
        properties?: { [property: string]: any },
        options?: { [option: string]: any }
      ) => void;
      reset: () => void;
      user: () => {
        id: () => string;
        traits: (traits?: any) => any;
        anonymousId: () => string;
      };
      // From https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
      // > "if you pass only one string to page it is assumed to be name"
      page: (pageName: string | undefined) => void;
      addSourceMiddleware: (
        middleware: (options: {
          payload: any;
          next: (payload: any) => unknown;
          integrations: any;
        }) => void
      ) => void;
      load: () => void;
    };
  }
}

export const ETH_DEPOSITED_TRAIT = 'eth_deposited';
const ETH_WITHDRAWN_TRAIT = 'eth_withdrawn';
const ETH_TRANSFERRED_TRAIT = 'eth_transferred';

// By default segment analytics.js caches the user traits and sends them
// with each identify call. As some of amplitude traits have special
// properties like increment (trait is incremented by the sent value
// on each identify call).
const TRAITS_TO_RESET = [
  ETH_DEPOSITED_TRAIT,
  ETH_WITHDRAWN_TRAIT,
  ETH_TRANSFERRED_TRAIT,
];

const resetUserTraits = () => {
  if (!window.analytics) return;

  const { user } = window.analytics;
  if (user) {
    const traits = user().traits();
    TRAITS_TO_RESET.forEach(t => delete traits[t]);
    user().traits(traits);
  }
};

type IdentifyArgs = {
  userId?: string;
  traits?: { [trait: string]: any };
};

const identify = ({ userId, traits }: IdentifyArgs): void => {
  if (!window.analytics) return;

  const { user } = window.analytics;
  if (!userId && !user) {
    console.warn('Unable to identify without a userId');
    return;
  }

  const uuid = userId || user().id();

  window.analytics.identify(uuid, traits);
  if ((!isProduction || devEnv) && import.meta.env.MODE !== 'test') {
    console.log('🪲 Analytics User Identified: ', uuid, traits);
  }

  resetUserTraits();
};

function hasAmplitudeEnabled() {
  const { destinations } = loadCookie('tracking-preferences2') ?? {};
  return Boolean(
    destinations?.Amplitude || destinations?.['Actions Amplitude']
  );
}

const SessionReplayManager = ({
  useRecordSessions,
  onSessionRollover,
}: {
  useRecordSessions: boolean;
  onSessionRollover: () => void;
}) => {
  useEffect(() => {
    if (!useRecordSessions || !hasAmplitudeEnabled()) {
      return undefined;
    }

    import('@amplitude/session-replay-browser').then(s => {
      let storedSessionId = loadCookie('analytics_session_id') || 0;
      sessionReplay = s;

      window.analytics.ready(() => {
        sessionReplay?.init(AMPLITUDE_API_KEY, {
          deviceId: window.analytics.user().anonymousId(),
          sessionId: storedSessionId,
          sampleRate: 1, // 100% of sessions under feature flag
        });
      });

      window.analytics.addSourceMiddleware(({ payload, next }) => {
        const nextSessionId = loadCookie('analytics_session_id') || 0;
        if (storedSessionId < nextSessionId) {
          storedSessionId = nextSessionId;
          // Trigger a remount via key change so useRecordPath re-evaluates
          // from scratch — if the user is off-path, recording won't restart.
          onSessionRollover();
        }
        next(payload);
      });
    });

    return () => {
      if (sessionReplay) {
        sessionReplay.shutdown();
        sessionReplay = undefined;
      }
    };
  }, [useRecordSessions, onSessionRollover]);

  return null;
};

export const EventsProvider = ({ children }: Props) => {
  const { isAndroidApp, postMessage } = useIsMobileApp();
  const {
    flags: { useRecordSessions: useRecordSessionsFlag = false },
  } = useFeatureFlags();

  const [sessionVersion, setSessionVersion] = useState(0);
  const handleSessionRollover = useCallback(() => {
    setSessionVersion(v => v + 1);
  }, []);
  const matchesUrl = useRecordPath();

  const useRecordSessions = useRecordSessionsFlag || matchesUrl;

  const value = useMemo(
    () => ({
      identify: (id: string, traits?: { [trait: string]: any }) =>
        identify({ userId: id, traits }),
      track: (event: string, properties?: { [propery: string]: any }): void => {
        if (window.analytics) {
          if (useRecordSessions && sessionReplay) {
            window.analytics.track(event, {
              ...sessionReplay.getSessionReplayProperties(),
              ...(properties || {}),
            });
          } else {
            window.analytics.track(event, properties);
          }
        }

        if (isAndroidApp) {
          postMessage('trackInAppEvent', {
            event,
            jsonProperties: JSON.stringify(properties),
          });
        }

        if ((!isProduction || devEnv) && import.meta.env.MODE !== 'test') {
          console.log('🪲 Analytics Event Tracked: "%s"', event, properties);
        }
      },
    }),
    [isAndroidApp, postMessage, useRecordSessions]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      <SessionReplayManager
        key={sessionVersion}
        useRecordSessions={useRecordSessions}
        onSessionRollover={handleSessionRollover}
      />
      {children}
    </AnalyticsContext.Provider>
  );
};
